import { Box, Text } from 'grommet';
import { useTranslation } from 'react-i18next';
import SyncTable from './SyncErrorModal/SyncTable';
import ConfirmationModal from './ConfirmationModal';
import { showToast } from '../../service/common/utils';
import { useDispatch } from 'react-redux';
import { getPracticeData, upsertShapefile } from '../../../../service/map/map.service';
import { saveFields } from '../../../../redux/actions/map-actions/field/field-action';
import { calculateCarbonEstimate } from '../../../../service/map/estimate.service';
import { useAppSelector } from '../../../../redux';

interface SyncFieldsModalProps {
  setShowFinalizeModal: (value: ((prevState: boolean) => boolean) | boolean) => void;
  setShowLoading: (value: ((prevState: boolean) => boolean) | boolean) => void;
}

const SyncFieldsModal = ({ setShowFinalizeModal, setShowLoading }: SyncFieldsModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const totalFieldsArea = useAppSelector((state) => {
    const country = state.producer.producerInformation?.country ?? 'us';
    return state.map.totalFieldsArea[country === 'br' ? 'areaHectare' : 'areaAcre'];
  });
  const { fields, deletedFields, updatedFieldBoundaries } = useAppSelector((state) => {
    return {
      fields: state.map.fields,
      deletedFields: state.map.deletedFields,
      updatedFieldBoundaries: state.map.updatedFieldBoundaries,
    };
  });
  const { journey, country, contractPaymentOption } = useAppSelector((state) => {
    return {
      journey: state.producer.journeyInformation,
      country: state.producer.producerInformation?.country ?? 'us',
      contractPaymentOption: state.map.contractPaymentOption,
    };
  });
  const estimate = useAppSelector((state) => state.map.estimate);

  async function handleSyncFields() {
    setShowFinalizeModal(false);
    // stepForward();
    await dispatch(saveFields(journey, fields, deletedFields, updatedFieldBoundaries));
    setShowLoading(true);
    try {
      let totalCarbon: number | undefined = undefined;
      if (country === 'br') {
        const calculatedResult = await calculateCarbonEstimate(estimate, country);
        // Dividing by 3.67 to convert from CO2 to C
        totalCarbon = calculatedResult.calculatorResultCombined.carbon.totalCarbon / 3.67;
      }
      // TODO: check if totalCarbon is used anywhere
      await upsertShapefile(journey, 'APPROVED', country, contractPaymentOption, totalCarbon);
      showToast(t('fieldFlow.success.success'), t('fieldFlow.success.sync'), 'success', 'sync-success');
    } catch (e) {
      showToast(t('fieldFlow.errors.error'), t('fieldFlow.errors.sync'), 'error', 'sync-error');
    } finally {
      setShowLoading(false);
    }
  }

  return (
    <ConfirmationModal
      dataCy={'sync-fields-modal'}
      confirm={async () => await handleSyncFields()}
      cancel={() => setShowFinalizeModal(false)}
      title={t('fieldFlow.syncModal.title')}
      width={'708px'}
      message={
        <Box>
          <Text weight={700} textAlign={'center'} color={'#D03450'}>
            {t('fieldFlow.syncModal.afterSync')}
          </Text>
          <Text weight={700} textAlign={'center'} color={'#D03450'}>
            {t('fieldFlow.syncModal.areYouSure')}
          </Text>
          <Text weight={700} margin={{ top: '16px', bottom: '8px' }} textAlign={'center'}>
            {t('fieldFlow.syncModal.currentData')} ({t('fieldFlow.totalAcreage')}: {totalFieldsArea} {t('fieldFlow.acreUnit')})
          </Text>
          <SyncTable practiceData={getPracticeData(fields.filter((i) => !i?.properties?.unenrolledAt))} type={'currentData'} />
        </Box>
      }
      confirmLabel={t('fieldFlow.syncModal.confirm')}
      cancelLabel={t('fieldFlow.syncModal.cancel')}
    />
  );
};

export default SyncFieldsModal;
